"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { AnimatePresence, motion } from "motion/react"
import { IconXmarkOutline18 as XIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import { easeOut, easeOutQuint } from "../../helpers/animations"
import { IconButton } from "../icon-button"

export const InternalDialog = React.memo(DialogPrimitive.Root)
export const InternalDialogTrigger = React.memo(DialogPrimitive.Trigger)

export const InternalDialogPortal = React.memo(
	({ ...props }: DialogPrimitive.DialogPortalProps) => (
		<DialogPrimitive.Portal {...props} />
	),
)

export const InternalDialogOverlay = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DialogPrimitive.Overlay>,
		React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
	>(({ className, ...props }, ref) => (
		<DialogPrimitive.Overlay
			ref={ref}
			className={cn(
				"fixed inset-0 z-50 bg-white/40 dark:bg-gray-950/80",
				className,
			)}
			{...props}
		/>
	)),
)

export type DialogSize = "default" | "lg" | "sm" | "xl"
export type DialogPosition = "center" | "top" | "right"

const overlayVariants = {
	hidden: { opacity: 0 },
	visible: { opacity: 1 },
}

const centerVariants = {
	hidden: { opacity: 0, scale: 0.96 },
	visible: { opacity: 1, scale: 1 },
}

const rightVariants = {
	hidden: { opacity: 0, x: 24 },
	visible: { opacity: 1, x: 0 },
}

export const InternalDialogContent = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DialogPrimitive.Content>,
		React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
			size?: DialogSize
			position: DialogPosition
			showCloseButton?: boolean
			withOverlay?: boolean
			darkOverlay?: boolean
			contentPadding?: string
			isOpen?: boolean
		}
	>(
		(
			{
				className,
				position,
				showCloseButton,
				children,
				size = "default",
				withOverlay = true,
				darkOverlay = false,
				contentPadding = "p-6",
				isOpen,
				...props
			},
			ref,
		) => {
			const isRight = position === "right"
			const variants = isRight ? rightVariants : centerVariants

			return (
				<AnimatePresence>
					{isOpen && (
						<DialogPrimitive.Portal forceMount>
							{withOverlay && (
								<DialogPrimitive.Overlay forceMount asChild>
									<motion.div
										variants={overlayVariants}
										initial="hidden"
										animate="visible"
										exit="hidden"
										transition={{ duration: 0.15, ease: "easeInOut" }}
										className={cn(
											"fixed inset-0 z-50 dark:bg-gray-950/80",
											darkOverlay ? "bg-gray-950/50" : "bg-white/40",
										)}
									/>
								</DialogPrimitive.Overlay>
							)}
							<div
								className={cn(
									"pointer-events-none fixed inset-0 z-50 flex",
									position === "center" && "items-center justify-center",
									position === "top" && "items-start justify-center pt-[20vh]",
									position === "right" && "items-stretch justify-end",
								)}
							>
								<DialogPrimitive.Content
									forceMount
									asChild
									ref={ref}
									{...props}
								>
									<motion.div
										variants={variants}
										initial="hidden"
										animate="visible"
										exit="hidden"
										transition={
											isRight
												? { duration: 0.3, ease: easeOutQuint }
												: { duration: 0.2, ease: easeOut }
										}
										className={cn(
											"pointer-events-auto relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-3xl bg-[#FCFCFC] shadow-lg outline-none ring-1 ring-gray-900/20 md:w-full dark:bg-gray-900 dark:shadow-black dark:ring-white/10",
											contentPadding,
											size === "default" && "max-w-[min(32rem,94vw)]",
											size === "lg" && "max-w-[min(48rem,94vw)]",
											size === "sm" && "max-w-[min(24rem,95vw)]",
											size === "xl" && "max-w-[min(64rem,94vw)]",
											position === "right" &&
												"m-2 max-h-[100vh] max-w-[calc(100vw-1rem)] rounded-2xl sm:max-w-[40rem]",
											className,
										)}
									>
										{children}
										{showCloseButton && (
											<DialogPrimitive.Close
												asChild
												className="absolute right-4 top-4"
											>
												<IconButton variant="ghost" Icon={XIcon} />
											</DialogPrimitive.Close>
										)}
									</motion.div>
								</DialogPrimitive.Content>
							</div>
						</DialogPrimitive.Portal>
					)}
				</AnimatePresence>
			)
		},
	),
)

export const InternalDialogHeader = React.memo(
	({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
		<div
			className={cn("mb-1.5 flex flex-col space-y-2 text-left", className)}
			{...props}
		/>
	),
)

export const InternalDialogFooter = React.memo(
	({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
		<div
			className={cn(
				"mt-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end sm:space-x-2",
				className,
			)}
			{...props}
		/>
	),
)

export const InternalDialogTitle = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DialogPrimitive.Title>,
		React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
	>(({ className, ...props }, ref) => (
		<DialogPrimitive.Title
			ref={ref}
			className={cn(
				"text-base font-semibold text-gray-900",
				"dark:text-gray-50",
				className,
			)}
			{...props}
		/>
	)),
)

export const InternalDialogDescription = React.memo(
	React.forwardRef<
		React.ElementRef<typeof DialogPrimitive.Description>,
		React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
	>(({ className, ...props }, ref) => (
		<DialogPrimitive.Description
			ref={ref}
			className={cn("text-sm text-secondary", className)}
			{...props}
		/>
	)),
)
